// src/components/JarvislyComponents/CpfCnpjInput.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo, useRef } from 'react';
import { Input } from 'antd';
import { applyNumberMask, returnOnlyNumbers } from 'utils/helpers';
import countriesProfiles from 'assets/data/countriesProfiles.json';
import ValidationSuffix from 'components/jarvislyComponents/ValidationSuffix.jsx';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CpfCnpjInput = React.forwardRef(
  (
    {
      // deconstructed to avoid DOM warnings and to keep static suffix
      loading, // boolean | undefined
      status, // '', 'validating', 'success', 'error' | undefined
      allowClear, // do not forward to avoid AntD dynamic suffix
      prefix,
      suffix, // optional external suffix kept stable
      ...props
    },
    ref
  ) => {
    // ─────────────────────────────────────────────────────────────────────────────
    // 1. PROPS, CONTEXTS & GLOBAL STATE
    // ─────────────────────────────────────────────────────────────────────────────
    const { onChange, profile, onBlur, autoFocus, disabled, countryCode = 'BR' } = props;

    // ─────────────────────────────────────────────────────────────────────────────
    // 2. HOOKS & REFS
    // ─────────────────────────────────────────────────────────────────────────────
    // keep prefix stable to avoid DOM restructure / focus loss
    const stablePrefixRef = useRef(prefix ?? null);

    // ─────────────────────────────────────────────────────────────────────────────
    // 3. LOCAL VARIABLES I
    // ─────────────────────────────────────────────────────────────────────────────
    // When profile='auto', documentType is null to enable auto-detection based on digit count
    const documentType = profile
      ? profile?.toLowerCase() === 'auto'
        ? null // auto-detection mode
        : ['cnpj', 'ein'].includes(profile?.toLowerCase())
          ? 'ein'
          : 'ssn'
      : null;

    let maxLength = 14;
    let value = $buildValueMaks();

    // static suffix (never unmounts)
    const staticSuffix = useMemo(() => {
      return <ValidationSuffix status={status || ''} loading={!!loading} extraSuffix={suffix} />;
    }, [status, loading, suffix]);

    // ─────────────────────────────────────────────────────────────────────────────
    // 4. LOCAL STATE
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // 5. EFFECTS
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // 6. HANDLERS
    // ─────────────────────────────────────────────────────────────────────────────
    const onLocalChange = ev => {
      const valueNumber = returnOnlyNumbers(ev?.target?.value);
      const mask = getDocMaskByNumberLength(valueNumber, documentType, countryCode);

      const nextLength = valueNumber?.length || 0;
      if (nextLength > maxLength) return;

      value = $buildValueMaks(valueNumber);

      // keep upstream change logic stable
      onChange && onChange(ev);
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // 7. LOCAL VARIABLES II
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // 8. UI
    // ─────────────────────────────────────────────────────────────────────────────
    return (
      <Input
        {...props}
        ref={ref}
        value={value}
        onBlur={onBlur}
        autoFocus={autoFocus}
        prefix={stablePrefixRef.current}
        suffix={staticSuffix}
        // IMPORTANT: do not pass allowClear to avoid dynamic suffix from AntD
        disabled={disabled}
        onChange={onLocalChange}
        status={status || undefined}
      />
    );

    // *******************************************************************************
    // HELPERS (hoisted)
    // *******************************************************************************
    function $buildValueMaks(forcedNumber) {
      const sc = countriesProfiles
        ?.filter(c => c.enabled)
        ?.find(c => c.countryCode === countryCode);
      const masks = sc?.documentMasks || { ssn: '999.999.999-99', ein: '99.999.999/9999-99' };
      const digits =
        documentType && masks?.[documentType]
          ? returnOnlyNumbers(masks[documentType])
          : returnOnlyNumbers(masks?.ein || '99.999.999/9999-99');

      maxLength = digits.length;

      // Handle both string and object values (e.g., { value: '123', countryCode: 'BR' })
      // When form stores CNPJ as object from external API, extract the .value property
      const rawValue = typeof props?.value === 'object' && props?.value !== null
        ? props.value.value
        : props.value;
      const val = forcedNumber ?? (rawValue && returnOnlyNumbers(rawValue));
      if (val) {
        return applyNumberMask(
          val,
          masks[getDocMaskByNumberLength(val, documentType, countryCode)]
        );
      }
      return '';
    }
  }
);

// *******************************************************************************
// EXPORT
// *******************************************************************************
export const getDocMaskByNumberLength = (value, documentType, countryCode) => {
  if (documentType && ['cnpj', 'ein', 'cpf', 'ssn'].includes(documentType?.toLowerCase())) {
    return ['cnpj', 'ein'].includes(documentType?.toLowerCase()) ? 'ein' : 'ssn';
  }
  if (countryCode === 'BR') {
    return value?.length > 11 ? 'ein' : 'ssn';
  }
  return 'ein';
};

export default CpfCnpjInput;

// *******************************************************************************
// HELPERS
// *******************************************************************************
